'use client';

import { Button } from '../../components/button';
import { Text } from '../../components/text';
import {
  ConfirmationDialog,
  type ConfirmationDialogVariant,
} from '../confirmation-dialog';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { InformationCircleIcon } from '@terra/ui-icons';
import { useState } from 'react';
import { SettingsCard } from './SettingsCard';
import { SettingsRow } from './SettingsRow';

export type SettingsDangerSectionSeverity = 'destructive' | 'neutral';
export type SettingsDangerSectionProps = {
  rowLabel: string;
  actionLabel: string;
  warningTitle: string;
  warningDescription: string;
  confirmTitle: string;
  confirmDescription: string;
  confirmText: string;
  cancelText?: string;
  onAction: () => void | Promise<void>;
  loading?: boolean;
  actionDisabled?: boolean;
  severity?: SettingsDangerSectionSeverity;
  showCard?: boolean;
};
const SEVERITY_STYLES: Record<
  SettingsDangerSectionSeverity,
  {
    alertBg?: string;
    alertBorder?: string;
    iconColor?: string;
    dialogVariant: ConfirmationDialogVariant;
    buttonVariant?: 'outline';
    buttonColorScheme?: 'red';
    buttonBg?: string;
    buttonColor?: string;
  }
> = {
  destructive: {
    alertBg: 'bg.vulnerability.vulnerable',
    alertBorder: 'border.vulnerability.critical',
    iconColor: 'bg.graphs.critical',
    dialogVariant: 'destructive',
    buttonBg: 'bg.graphs.critical',
    buttonColor: 'fg.on-accent.default',
  },
  neutral: {
    alertBorder: 'border.neutral.subtle',
    iconColor: 'fg.neutral.muted',
    dialogVariant: 'default',
    buttonVariant: 'outline',
  },
};

export function SettingsDangerSection({
  rowLabel,
  actionLabel,
  warningTitle,
  warningDescription,
  confirmTitle,
  confirmDescription,
  confirmText,
  cancelText,
  onAction,
  loading,
  actionDisabled,
  severity = 'destructive',
  showCard = true,
}: SettingsDangerSectionProps) {
  const [open, setOpen] = useState(false);
  const styles = SEVERITY_STYLES[severity];
  const actionButton = (
    <Button
      size="sm"
      onClick={() => setOpen(true)}
      loading={loading}
      disabled={actionDisabled}
      {...(styles.buttonVariant ? { variant: styles.buttonVariant } : {})}
      {...(styles.buttonColorScheme
        ? { colorScheme: styles.buttonColorScheme }
        : styles.buttonBg
          ? {
              bg: styles.buttonBg,
              color: styles.buttonColor,
              shadow: 'xs',
            }
          : {})}
    >
      {actionLabel}
    </Button>
  );
  const alertContent = (
    <VStack alignItems="flex-start" gap={2} minW={0} w="full">
      <Text fontSize="sm" fontWeight="medium" color="fg.neutral.default">
        {warningTitle}
      </Text>
      <Text fontSize="sm" color="fg.neutral.muted">
        {warningDescription}
      </Text>
    </VStack>
  );
  const showAlertPanel = Boolean(styles.iconColor ?? styles.alertBg);
  const actionRow = showCard ? (
    <SettingsCard>
      <SettingsRow label={rowLabel} control={actionButton} />
    </SettingsCard>
  ) : (
    <SettingsRow label={rowLabel} control={actionButton} />
  );

  return (
    <>
      <VStack alignItems="stretch" gap={5} w="full">
        {showAlertPanel ? (
          <HStack
            alignItems="flex-start"
            gap={4}
            px={4}
            py={2}
            bg={styles.alertBg}
            borderWidth="1px"
            borderColor={styles.alertBorder ?? 'border.neutral.subtle'}
            borderRadius="lg"
            w="full"
          >
            <Box
              color={styles.iconColor ?? 'fg.neutral.muted'}
              flexShrink={0}
              mt={0.5}
            >
              <InformationCircleIcon size={20} />
            </Box>
            {alertContent}
          </HStack>
        ) : (
          alertContent
        )}
        {actionRow}
      </VStack>
      <ConfirmationDialog
        isOpen={open}
        onOpenChange={({ open: nextOpen }) => setOpen(nextOpen)}
        onConfirm={onAction}
        loading={loading}
        variant={styles.dialogVariant}
        title={confirmTitle}
        description={confirmDescription}
        confirmText={confirmText}
        cancelText={cancelText}
        maxWidth="500px"
        showIcon={false}
      />
    </>
  );
}
